import * as React from 'react'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { useFormik } from 'formik'
import { Col } from 'antd'
import { colors } from 'web-makeup/dist/design-system/config/data/theme'
import { map } from 'lodash'

import { UPDATE_BRAND_PROFILE } from '../../../../../mutations/UpdateBrandProfile'
import { ADD_DIVISION } from '../../../../../mutations/AddDivision'
import { DELETE_DIVISION } from '../../../../../mutations/DeleteDivision'
import { ADD_SUBBRAND } from '../../../../../mutations/AddSubbrand'
import { DELETE_SUBBRAND } from '../../../../../mutations/DeleteSubbrand'
import { ALL_ORGANISATIONS } from '../../../../../queries/AllOrganisations'
import { client as apolloClient } from '../../../../ApolloProvider/ApolloProvider'
import { Brand as BrandType, BrandDepartment } from '../../../../../typings/types'

import DefaultButton from '../../../components/DefaultButton/DefaultButton'
import MultiSelect from '../../MultiSelect'
import { BrandDepartmentLabels, regionOption } from '../../config'

import {
  PageTitle,
  WhiteBox,
  InputLabel,
  Input,
  ButtonWrapper,
  StyledRow,
  StyledSelect,
  Tag,
  TagsWrapper,
  ClearElem,
} from '../../BrandDashboardSettings.style'

interface BrandOrganizationStructureFormProps {
  brandProfile?: BrandType
  brandOrderUuid?: string
}

const BrandOrganizationStructureForm: React.FC<BrandOrganizationStructureFormProps> = ({
  brandProfile,
  brandOrderUuid,
}) => {
  const [
    brandOrganizationStructureInitialState,
    useBrandOrganizationStructureInitialState,
  ] = React.useState({
    ultimateParentBrandId: '',
    ultimateParentBrandName: '',
    parentBrandId: '',
    parentBrandName: '',
    department: '',
    divisions: [] as string[],
    divisionId: '',
    subbrands: [] as string[],
    subbrandsId: '',
  })
  const [regionsState, setRegionsState] = React.useState<any>([])
  const [divisionsValue, setDivisionsValue] = React.useState('')
  const [brandsValue, setBrandsValue] = React.useState('')

  const [updateBrandProfile, { loading: brandDetailsUpdating }] = useMutation(
    UPDATE_BRAND_PROFILE,
    {
      client: apolloClient,
    }
  )
  const [addDivision] = useMutation(ADD_DIVISION, {
    client: apolloClient,
  })
  const [deleteDivision] = useMutation(DELETE_DIVISION, {
    client: apolloClient,
  })
  const [addSubBrandsMutation] = useMutation(ADD_SUBBRAND, {
    client: apolloClient,
  })
  const [deleteSubbrandMutation] = useMutation(DELETE_SUBBRAND, {
    client: apolloClient,
  })

  const { data: allOrganisations, refetch: refetchOrganisations } = useQuery(ALL_ORGANISATIONS, {
    variables: { keyword: '', includeOrganisationsWithoutProjects: true },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    client: apolloClient,
  })

  const regionsDefaultOptions: any = regionOption.filter(obj => {
    return regionsState && regionsState.some(v => obj.label === v)
  })

  const parentBrandDivisions =
    brandProfile &&
    ((brandProfile.ultimateParent &&
      brandProfile.ultimateParent.divisions &&
      brandProfile.ultimateParent.divisions.length > 0 &&
      brandProfile.ultimateParent.divisions) ||
      (brandProfile.parent &&
        brandProfile.parent.divisions &&
        brandProfile.parent.divisions.length > 0 &&
        brandProfile.parent.divisions))

  const parentBrandSubbrands =
    brandProfile &&
    ((brandProfile.ultimateParent && brandProfile.ultimateParent.subbrands) ||
      (brandProfile.parent && brandProfile.parent.subbrands))

  const isPartOfParrentSB = () => {
    if (
      parentBrandSubbrands &&
      brandProfile &&
      parentBrandSubbrands.some(e => e.id === brandProfile.subbrandId)
    ) {
      return true
    }
    return false
  }
  const isPartOfParrentDiv = () => {
    if (
      parentBrandDivisions &&
      brandProfile &&
      parentBrandDivisions.some(e => brandProfile.division && e.id === brandProfile.division.id)
    ) {
      return true
    }
    return false
  }

  React.useEffect(
    () => {
      useBrandOrganizationStructureInitialState({
        ultimateParentBrandId:
          (brandProfile && brandProfile.ultimateParent && brandProfile.ultimateParent.uuid) || '',
        ultimateParentBrandName:
          (brandProfile &&
            brandProfile.ultimateParent &&
            brandProfile.ultimateParent.tradingName) ||
          '',
        parentBrandId: (brandProfile && brandProfile.parent && brandProfile.parent.uuid) || '',
        parentBrandName:
          (brandProfile && brandProfile.parent && brandProfile.parent.tradingName) || '',
        department: (brandProfile && brandProfile.department) || '',
        divisions:
          (brandProfile &&
            brandProfile.divisions &&
            brandProfile.divisions.map(item => {
              return item.name
            })) ||
          [],
        divisionId: isPartOfParrentDiv()
          ? (brandProfile && brandProfile.division && brandProfile.division.id) || ''
          : '',
        subbrands:
          (brandProfile &&
            brandProfile.subbrands &&
            brandProfile.subbrands.map(item => {
              return item.name
            })) ||
          [],
        subbrandsId: isPartOfParrentSB() ? (brandProfile && brandProfile.subbrandId) || '' : '',
      })

      brandProfile &&
        brandProfile.regions &&
        setRegionsState(brandProfile.regions.map(item => item.name))
    },
    [brandProfile]
  )

  const brandOrganizationStructureformik = useFormik({
    initialValues: {
      ...brandOrganizationStructureInitialState,
      regions: regionsDefaultOptions && regionsDefaultOptions.map(e => e.value),
    },
    enableReinitialize: true,
    onSubmit: values => {
      updateBrandProfile({
        variables: {
          brandUuid: brandOrderUuid,
          brandUpdateInput: {
            ultimateParentId: values.ultimateParentBrandId,
            parentID: values.parentBrandId,
            regionIds: values.regions.map(i => Number(i)),
            department: values.department !== '' ? values.department : null,
            divisionId: Number(values.divisionId) || null,
            subbrandId: Number(values.subbrandsId) || null,
          },
        },
      })
    },
  })

  const closeIcon = (
    <svg
      height="14"
      width="14"
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
      className="css-tj5bde-Svg"
    >
      <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
    </svg>
  )
  const addDivisions = e => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault()
      if (!brandOrganizationStructureInitialState.divisions.includes(divisionsValue)) {
        useBrandOrganizationStructureInitialState({
          ...brandOrganizationStructureInitialState,
          divisions: brandOrganizationStructureInitialState.divisions.concat(divisionsValue),
        })
        if (brandProfile) {
          addDivision({
            variables: {
              brandUuid: brandOrderUuid,
              name: divisionsValue,
            },
          })
        }
      }
      setDivisionsValue('')
    }
  }
  const removeDivisions = key => {
    useBrandOrganizationStructureInitialState({
      ...brandOrganizationStructureInitialState,
      divisions: brandOrganizationStructureInitialState.divisions.filter(item => item !== key),
    })
    const divisionId =
      brandProfile &&
      brandProfile.divisions &&
      brandProfile.divisions.find(item => item && item.name === key)
    divisionId && deleteDivision({ variables: { divisionId: divisionId.id } })
  }

  const addSubBrands = e => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault()
      if (!brandOrganizationStructureInitialState.subbrands.includes(brandsValue)) {
        useBrandOrganizationStructureInitialState({
          ...brandOrganizationStructureInitialState,
          subbrands: brandOrganizationStructureInitialState.subbrands.concat(brandsValue),
        })
        if (brandProfile) {
          addSubBrandsMutation({
            variables: {
              brandUuid: brandOrderUuid,
              name: brandsValue,
            },
          })
        }
      }
      setBrandsValue('')
    }
  }
  const removeSubBrands = key => {
    useBrandOrganizationStructureInitialState({
      ...brandOrganizationStructureInitialState,
      subbrands: brandOrganizationStructureInitialState.subbrands.filter(item => item !== key),
    })
    const subBrandId =
      brandProfile &&
      brandProfile.subbrands &&
      brandProfile.subbrands.find(item => item && item.name === key)
    subBrandId && deleteSubbrandMutation({ variables: { subbrandId: subBrandId.id } })
  }

  const isParentBrand =
    brandProfile && (brandProfile.parent === null && brandProfile.ultimateParent === null)

  const clearUltimateParentBrand = () => {
    useBrandOrganizationStructureInitialState({
      ...brandOrganizationStructureInitialState,
      ultimateParentBrandId: '',
      ultimateParentBrandName: '',
    })
  }

  const clearParentBrand = () => {
    useBrandOrganizationStructureInitialState({
      ...brandOrganizationStructureInitialState,
      parentBrandId: '',
      parentBrandName: '',
    })
  }

  const { Option } = StyledSelect
  return (
    <>
      <PageTitle>Organization structure</PageTitle>
      <form onSubmit={brandOrganizationStructureformik.handleSubmit}>
        <WhiteBox>
          <StyledRow type="flex" gutter={24}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Ultimate parent brand</InputLabel>
              {brandOrganizationStructureformik.values.ultimateParentBrandId !== '' && (
                <ClearElem onClick={() => clearUltimateParentBrand()}>Clear</ClearElem>
              )}
              <StyledSelect
                className={`${
                  brandOrganizationStructureformik.values.ultimateParentBrandId === ''
                    ? ''
                    : 'filled'
                } parent-brand`}
                name="ultimateParentBrandId"
                onSearch={value =>
                  refetchOrganisations({
                    keyword: value,
                    includeOrganisationsWithoutProjects: true,
                  })
                }
                onChange={value => {
                  brandOrganizationStructureformik.setFieldValue('ultimateParentBrandId', value)
                  useBrandOrganizationStructureInitialState({
                    ...brandOrganizationStructureInitialState,
                    ultimateParentBrandId: value,
                    ultimateParentBrandName: '',
                  })
                }}
                value={brandOrganizationStructureformik.values.ultimateParentBrandId}
                showSearch
                placeholder="Add an ultimate parent brand if any"
                suffixIcon={
                  <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                }
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {allOrganisations &&
                allOrganisations.allOrganisations &&
                allOrganisations.allOrganisations.length > 0 ? (
                  allOrganisations.allOrganisations.map(item => (
                    <Option value={item.uuid}>{item.tradingName}</Option>
                  ))
                ) : (
                  <Option value={brandOrganizationStructureInitialState.ultimateParentBrandId}>
                    {brandOrganizationStructureInitialState.ultimateParentBrandName &&
                      brandOrganizationStructureInitialState.ultimateParentBrandName}
                  </Option>
                )}
              </StyledSelect>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Parent brand</InputLabel>
              {brandOrganizationStructureformik.values.parentBrandId !== '' && (
                <ClearElem onClick={() => clearParentBrand()}>Clear</ClearElem>
              )}
              <StyledSelect
                className={`${
                  brandOrganizationStructureformik.values.parentBrandId === '' ? '' : 'filled'
                } parent-brand`}
                name="parentBrandId"
                onSearch={value =>
                  refetchOrganisations({
                    keyword: value,
                    includeOrganisationsWithoutProjects: true,
                  })
                }
                onChange={value => {
                  brandOrganizationStructureformik.setFieldValue('parentBrandId', value)
                  useBrandOrganizationStructureInitialState({
                    ...brandOrganizationStructureInitialState,
                    parentBrandId: value,
                    ultimateParentBrandName: '',
                  })
                }}
                value={brandOrganizationStructureformik.values.parentBrandId}
                showSearch
                placeholder="Add a parent brand if any"
                suffixIcon={
                  <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                }
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().includes(input.toLowerCase())
                }
              >
                {allOrganisations &&
                allOrganisations.allOrganisations &&
                allOrganisations.allOrganisations.length > 0 ? (
                  allOrganisations.allOrganisations.map(item => (
                    <Option value={item.uuid}>{item.tradingName}</Option>
                  ))
                ) : (
                  <Option value={brandOrganizationStructureInitialState.parentBrandId}>
                    {brandOrganizationStructureInitialState.parentBrandName &&
                      brandOrganizationStructureInitialState.parentBrandName}
                  </Option>
                )}
              </StyledSelect>
            </Col>
          </StyledRow>
          <StyledRow type="flex">
            <Col xs={24} sm={24} md={24} lg={24}>
              <InputLabel>Regions</InputLabel>
              <MultiSelect
                className={brandOrganizationStructureformik.values.regions === '' ? '' : 'filled'}
                options={regionOption}
                selectValue={regionsDefaultOptions}
                name="regions"
                placeholder="Pick a new region"
                onChange={value => setRegionsState(value.map(item => item.label))}
              />
            </Col>
          </StyledRow>
          <StyledRow type="flex" gutter={24}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Department</InputLabel>
              <StyledSelect
                className={
                  brandOrganizationStructureformik.values.department === '' ? '' : 'filled'
                }
                name="department"
                value={brandOrganizationStructureformik.values.department}
                onChange={value => {
                  brandOrganizationStructureformik.setFieldValue('department', value)
                  useBrandOrganizationStructureInitialState({
                    ...brandOrganizationStructureInitialState,
                    department: value,
                  })
                }}
                placeholder="Pick a department"
                suffixIcon={
                  <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                }
              >
                {map(BrandDepartment, (obj, index) => (
                  <Option key={index} value={BrandDepartment[obj]}>
                    {BrandDepartmentLabels[obj]}
                  </Option>
                ))}
              </StyledSelect>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Divisions</InputLabel>
              {isParentBrand ? (
                <>
                  <Input
                    type="text"
                    placeholder="Pick a division"
                    name="divisions"
                    onKeyDown={e => addDivisions(e)}
                    onChange={e => setDivisionsValue(e.target.value)}
                    value={divisionsValue}
                  />
                  <TagsWrapper>
                    {brandOrganizationStructureInitialState.divisions &&
                      brandOrganizationStructureInitialState.divisions.length > 0 &&
                      brandOrganizationStructureInitialState.divisions.map(e => (
                        <Tag key={e} onClick={() => removeDivisions(e)}>
                          {e}
                          {closeIcon}
                        </Tag>
                      ))}
                  </TagsWrapper>
                </>
              ) : (
                <StyledSelect
                  className={
                    brandOrganizationStructureformik.values.divisionId === '' ? '' : 'filled'
                  }
                  name="division"
                  value={brandOrganizationStructureformik.values.divisionId}
                  onChange={value => {
                    brandOrganizationStructureformik.setFieldValue('division', value)
                    useBrandOrganizationStructureInitialState({
                      ...brandOrganizationStructureInitialState,
                      divisionId: value,
                    })
                  }}
                  placeholder="Pick a division"
                  suffixIcon={
                    <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                  }
                >
                  {parentBrandDivisions &&
                    parentBrandDivisions.map(obj => (
                      <Option key={obj.id} value={obj.id}>
                        {obj.name}
                      </Option>
                    ))}
                </StyledSelect>
              )}
            </Col>
          </StyledRow>
          <StyledRow type="flex" gutter={24}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <InputLabel>Brands</InputLabel>
              {isParentBrand ? (
                <>
                  <Input
                    type="text"
                    placeholder="Add related brands"
                    name="brands"
                    onKeyDown={e => addSubBrands(e)}
                    onChange={e => setBrandsValue(e.target.value)}
                    value={brandsValue}
                  />
                  <TagsWrapper>
                    {brandOrganizationStructureInitialState.subbrands &&
                      brandOrganizationStructureInitialState.subbrands.length > 0 &&
                      brandOrganizationStructureInitialState.subbrands.map(e => (
                        <Tag key={e} onClick={() => removeSubBrands(e)}>
                          {e}
                          {closeIcon}
                        </Tag>
                      ))}
                  </TagsWrapper>
                </>
              ) : (
                <StyledSelect
                  className={
                    brandOrganizationStructureformik.values.subbrandsId === '' ? '' : 'filled'
                  }
                  name="brands"
                  value={brandOrganizationStructureformik.values.subbrandsId}
                  onChange={value => {
                    brandOrganizationStructureformik.setFieldValue('subbrandsId', value)
                    useBrandOrganizationStructureInitialState({
                      ...brandOrganizationStructureInitialState,
                      subbrandsId: value,
                    })
                  }}
                  placeholder="Add related brand"
                  suffixIcon={
                    <img src="https://res.cloudinary.com/ninety-seconds/image/upload/v1632366093/-design-system/icon-arrow-down-black.svg" />
                  }
                >
                  {parentBrandSubbrands &&
                    parentBrandSubbrands.map(obj => (
                      <Option key={obj.id} value={obj.id}>
                        {obj.name}
                      </Option>
                    ))}
                </StyledSelect>
              )}
            </Col>
          </StyledRow>
          <ButtonWrapper>
            <DefaultButton
              fontSize="1rem"
              padding="0 1.625rem"
              backgroundColor={colors.adaptBlue}
              style={{ marginLeft: 'auto' }}
            >
              {brandDetailsUpdating ? 'Loading...' : 'Save changes'}
            </DefaultButton>
          </ButtonWrapper>
        </WhiteBox>
      </form>
    </>
  )
}

export default BrandOrganizationStructureForm



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/BrandDashboardSettings/forms/BrandOrganizationStructureForm/BrandOrganizationStructureForm.tsx